import { PathType, type InventoryResource } from '@repo/api-gw-sdk';
import { Base64 } from 'js-base64';

import { useRoles } from '@/contexts/useRoles';

import { TextLink } from '../shared/textLink';
import { AuthorizationTooltip } from '../tooltips/authorizationTooltip';

function trimLocation(location: string): string {
  // Trims the location to 45 characters, puts ellipsis in the middle
  if (location.length <= 45) {
    return location;
  }
  return `${location.slice(0, 20)}...${location.slice(-25)}`;
}

export const PathLink = ({
  entity,
  path,
  pathType,
  className,
}: {
  entity: InventoryResource;
  path: string;
  pathType: PathType;
  className?: string;
}) => {
  return pathType === PathType.File ? (
    <FileLink
      entity={entity}
      location={path}
      isWindowsMachine={isWindowsMachine([path])}
      className={className}
    />
  ) : (
    <DirLink entity={entity} location={path} className={className} />
  );
};

export function FileLink({
  entity,
  location,
  isWindowsMachine,
  className,
}: {
  entity: InventoryResource;
  location: string;
  isWindowsMachine: boolean;
  className?: string;
}) {
  if (isWindowsMachine) {
    if (location.startsWith('/')) {
      location = location.substring(1);
    }

    location = location.replace(/\//g, '\\');
  }

  return (
    <BasicLink
      entity={entity}
      label={location}
      href={`/explorer/${encodeURIComponent(entity.id)}?file=${Base64.encode(location)}`}
      className={className}
    />
  );
}

function DirLink({
  entity,
  location,
  className,
}: {
  entity: InventoryResource;
  location: string;
  className?: string;
}) {
  const href = `/explorer/${encodeURIComponent(entity.id)}?dir=${Base64.encode(location)}`;

  return (
    <BasicLink
      entity={entity}
      label={location}
      href={href}
      className={className}
    />
  );
}

const BasicLink = ({
  entity,
  label,
  href,
  className,
}: {
  entity: InventoryResource;
  label: string;
  href: string;
  className?: string;
}) => {
  const { isAuthorizedResource } = useRoles();
  const isAuthorized = isAuthorizedResource('read:files', entity);

  return (
    <AuthorizationTooltip isAuthorized={isAuthorized}>
      <span>
        <TextLink
          href={href}
          data-testid='file-explorer-link'
          disabled={!isAuthorizedResource('read:files', entity)}
          className={className}
        >
          {trimLocation(label) ?? 'Unknown'}
        </TextLink>
      </span>
    </AuthorizationTooltip>
  );
};

export const isWindowsMachine = (examples: string[]) =>
  examples
    .filter(Boolean)
    .some((example) =>
      [
        '/C:/',
        '/D:/',
        '/E:/',
        '/F:/',
        'C:/',
        'D:/',
        'E:/',
        'F:/',
        'C:\\',
        'D:\\',
        'E:\\',
        'F:\\',
      ].some((prefix) => example.toUpperCase()?.startsWith(prefix))
    );
